import { CLASS_ICON } from '../game/party-frames.ts';
import { t } from '../i18n.ts';
import { clear, h } from '../ui/dom.ts';
import { fmtDuration } from '../ui/format.ts';
import type { CampaignService, PartyInfo } from './service.ts';

/**
 * The rest of the group, one chip each (online campaigns): their colour and class, a green dot while they
 * have the campaign open, red while in a live room, else how long ago they played. In the bastion down
 * the left edge; in a waiting room too, so you know whether to wait for someone.
 */
// One line per room kind: a kind added to PartyInfo without its text doesn't compile (the saga's and the maze's were
// once shown as raw keys).
const ROOM_SAID: Record<PartyInfo['kind'], string> = {
  crypt: 'allies.in.crypt',
  incursion: 'allies.in.incursion',
  wave: 'allies.in.wave',
  saga: 'allies.in.saga',
  expedition: 'allies.in.expedition',
  maze: 'allies.in.maze',
};

export class AllyStrip {
  readonly el: HTMLElement;
  private key = '';
  /** Left out (in a waiting room, the ones already in it). */
  skip = new Set<string>();

  constructor(
    private svc: CampaignService,
    private onTap: (text: string) => void,
    cls = 'ally-strip',
  ) {
    this.el = h(`div.${cls}`, { 'data-test': 'ally-strip' });
  }

  render(): void {
    const svc = this.svc;
    const s = svc.state;
    const pres = svc.presence();
    if (svc.kind !== 'server' || !pres) {
      this.el.hidden = true;
      return;
    }
    const online = new Set(pres.online);
    const roomOf = (pid: string): PartyInfo | undefined => pres.rooms.find((r) => r.players.includes(pid));
    const now = svc.now();
    const allies = s.players
      .filter((p) => p.id !== svc.me && !p.left && !this.skip.has(p.id))
      .map((p) => ({ p, on: online.has(p.id), room: roomOf(p.id), ago: Math.max(0, now - p.lastActiveAt) }))
      .sort((a, b) => Number(b.on) - Number(a.on) || a.ago - b.ago);
    const said = (a: (typeof allies)[number]): string =>
      a.room ? t(ROOM_SAID[a.room.kind]) : a.on ? t('allies.online') : t('allies.seen', { t: fmtDuration(a.ago) });
    // Rebuilt only when something shown changes (minutes, not seconds, for the ones away).
    const key = allies.map((a) => `${a.p.id}:${a.on ? 1 : 0}:${a.room?.kind ?? ''}:${a.on ? 0 : Math.floor(a.ago / 60_000)}`).join('|');
    this.el.hidden = allies.length === 0;
    if (key === this.key) return;
    this.key = key;
    clear(this.el);
    for (const a of allies) {
      const status = a.room ? `.in-room.k-${a.room.kind}` : a.on ? '.on' : '';
      const full = `${a.p.displayName}${a.p.hero.name && a.p.hero.name !== a.p.displayName ? ` (${a.p.hero.name})` : ''} · ${t(`class.${a.p.hero.heroClass}`)} ${t('hero.level', { n: a.p.hero.level })} · ${said(a)}`;
      this.el.append(
        h(
          `button.ally-chip${status}`,
          { style: `--who:${a.p.color}`, 'aria-label': `${a.p.displayName} · ${said(a)}`, title: full, onclick: () => this.onTap(full) },
          h('span.ac-face', { html: CLASS_ICON[a.p.hero.heroClass] }),
          h('span.ac-dot'),
          h('span.ac-txt', {}, h('b', {}, a.p.displayName), h('span', {}, said(a))),
        ),
      );
    }
  }
}
